import type {
  ComponentGroup,
  Form,
  FormComponent,
  ValidationRuleItem
} from "types";

export const INTERNAL_DUP_PREFIX = "__dup_";

type ResponseRecord = Record<string, unknown>;

export type SignatureUsage = Map<string, { count: number; allBounded: boolean }>;

export const sanitizeKey = (value: string) =>
  value.replace(/[^a-zA-Z0-9_]+/g, "_");

export const extractIndex = (key: string) => {
  const idx = key.split("_").pop();
  return idx && /^\d{2}$/.test(idx) ? idx : null;
};

export const toIndex = (value: string | number) => {
  const numeric = typeof value === "number" ? value : parseInt(value, 10);
  return Number.isNaN(numeric) ? null : numeric;
};

export const formatIndex = (index: number) => index.toString().padStart(2, "0");

export const isDuplicableKey = (
  key: string,
  duplicablePrefixes?: Set<string>
) => {
  if (key.startsWith(INTERNAL_DUP_PREFIX)) {
    return true;
  }
  const idx = extractIndex(key);
  if (!idx) {
    return false;
  }
  if (!duplicablePrefixes) {
    return true;
  }
  const base = key.slice(0, -3);
  return duplicablePrefixes.has(base);
};

export const isDuplicableGroup = (group?: ComponentGroup | null) => {
  const baseComponent = group?.components?.[0];
  return Boolean(baseComponent?.duplicable && baseComponent?.bound);
};

export const getGroupSignature = (group: ComponentGroup) => {
  return (group.components || [])
    .filter(component => component.duplicable)
    .map(component => component.name)
    .join("|");
};

export const buildSignatureUsage = (formValue: Form): SignatureUsage => {
  const usage: SignatureUsage = new Map();
  Object.values(formValue.steps).forEach((step) => {
    step.sections.forEach((section) => {
      section.component_groups.forEach((group) => {
        if (!isDuplicableGroup(group)) {
          return;
        }
        const signature = getGroupSignature(group);
        const entry = usage.get(signature) || { count: 0, allBounded: true };
        entry.count += 1;
        const baseComponent = group.components?.[0];
        entry.allBounded =
          entry.allBounded &&
          Boolean(baseComponent?.bounded_type && group.group_info?.bounded_type);
        usage.set(signature, entry);
      });
    });
  });
  return usage;
};

export const getDesiredCount = (value: unknown, hasBoundValue: boolean) => {
  if (!hasBoundValue) {
    return 0;
  }
  let desiredCount = 1;
  if (value !== undefined && value !== null && value !== "") {
    const parsed = Number(value);
    desiredCount = Number.isNaN(parsed) ? 0 : parsed;
  }
  if (desiredCount < 0) {
    desiredCount = 0;
  }
  return desiredCount;
};

export const shouldShowByRules = (
  component: FormComponent,
  response: ResponseRecord,
  storedResponse: ResponseRecord
) => {
  if (!component?.validation_rules || component.validation_rules.length === 0) {
    return true;
  }
  const validations = component.validation_rules.reduce(
    (acc: boolean[], rule: ValidationRuleItem) => {
      if (rule.action !== "check_memory") {
        if (Array.isArray(rule.targets) && rule.targets.length > 0) {
          const existingTargets = rule.targets.filter(
            (target: string) => response[target] !== undefined
          );
          if (existingTargets.length > 0) {
            const regexpToCheck = new RegExp(rule.condition);
            const ruleResults = existingTargets.reduce(
              (ruleAcc: boolean[], target: string) => {
                ruleAcc.push(regexpToCheck.test(JSON.stringify(response[target])));
                return ruleAcc;
              },
              []
            );
            acc.push(!ruleResults.includes(false));
          }
        }
      } else if (Array.isArray(rule.targets)) {
        rule.targets.forEach((target: string) => {
          if (storedResponse[target] !== undefined) {
            const regexpToCheck = new RegExp(rule.condition);
            acc.push(regexpToCheck.test(JSON.stringify(storedResponse[target])));
          }
        });
      }
      return acc;
    },
    []
  );
  return !validations.includes(false);
};

export const resolveGroupMeta = ({
  group,
  signatureUsage,
  groupId,
  signature
}: {
  group: ComponentGroup;
  signatureUsage: SignatureUsage;
  groupId?: string;
  signature?: string;
}) => {
  const baseComponent = group?.components?.[0];
  const actualTypeKey = baseComponent?.bounded_type;
  const actualGroupType = group?.group_info?.bounded_type;
  let partitionKey = actualTypeKey;
  let partitionValue = actualGroupType;
  const resolvedSignature = signature || getGroupSignature(group);
  const signatureInfo = signatureUsage.get(resolvedSignature);
  const useSynthetic =
    signatureInfo && signatureInfo.count > 1 && !signatureInfo.allBounded;
  if (useSynthetic) {
    const resolvedGroupId = groupId || "unknown";
    partitionKey = `${INTERNAL_DUP_PREFIX}${sanitizeKey(resolvedSignature)}`;
    partitionValue = `__grp_${resolvedGroupId}`;
  }
  return {
    actualTypeKey,
    actualGroupType,
    partitionKey,
    partitionValue,
    usePartitionKey: Boolean(partitionKey && partitionValue),
    useActualTypeKey: Boolean(actualTypeKey && actualGroupType)
  };
};

export const collectTypeIndices = (
  responseSource: ResponseRecord,
  typeKey: string | undefined,
  typeValue: string | undefined
) => {
  if (!typeKey || !typeValue) {
    return [];
  }
  const indices = new Set<string>();
  Object.entries(responseSource).forEach(([key, value]) => {
    if (key.startsWith(`${typeKey}_`) && value === typeValue) {
      const idx = extractIndex(key);
      if (idx) {
        indices.add(idx);
      }
    }
  });
  return Array.from(indices);
};

type CollectGroupIndicesOptions = {
  group: ComponentGroup;
  response: ResponseRecord;
  partitionKey?: string;
  partitionValue?: string;
  actualTypeKey?: string;
  actualGroupType?: string;
  includeActualTypeKey?: boolean;
  includeComponentFallback?: boolean;
};

export const collectGroupIndices = ({
  group,
  response,
  partitionKey,
  partitionValue,
  actualTypeKey,
  actualGroupType,
  includeActualTypeKey = true,
  includeComponentFallback = true
}: CollectGroupIndicesOptions) => {
  const indices = new Set<string>();
  collectTypeIndices(response, partitionKey, partitionValue).forEach(idx =>
    indices.add(idx)
  );
  if (includeActualTypeKey) {
    collectTypeIndices(response, actualTypeKey, actualGroupType).forEach(idx =>
      indices.add(idx)
    );
  }
  const hasTypeKey = Boolean(partitionKey) ||
    (includeActualTypeKey && actualTypeKey);
  if (
    indices.size === 0 &&
    !hasTypeKey &&
    includeComponentFallback &&
    group.components?.length
  ) {
    group.components.forEach((component) => {
      if (!component.duplicable) {
        return;
      }
      Object.keys(response).forEach((key) => {
        if (key.startsWith(`${component.name}_`)) {
          const idx = extractIndex(key);
          if (idx) {
            indices.add(idx);
          }
        }
      });
    });
  }
  return Array.from(indices);
};

export const buildIndexSets = (
  group: ComponentGroup,
  mergedResponse: ResponseRecord,
  typeKey: string | undefined,
  groupType: string | undefined
) => {
  const indicesSet = new Set<number>();
  const blockedIndices = new Set<number>();
  const useTypeKey = Boolean(typeKey && groupType);
  const keys = Object.keys(mergedResponse);
  if (useTypeKey) {
    keys.forEach((key) => {
      if (key.startsWith(`${typeKey}_`)) {
        const idx = toIndex(key.split("_").pop() as string);
        if (idx === null) {
          return;
        }
        const value = mergedResponse[key];
        if (value === groupType) {
          indicesSet.add(idx);
        } else if (value !== undefined && value !== null && value !== "") {
          blockedIndices.add(idx);
        }
      }
    });
  } else {
    group.components.forEach((component) => {
      if (!component.duplicable) {
        return;
      }
      keys.forEach((key) => {
        if (key.startsWith(`${component.name}_`)) {
          const idx = toIndex(key.split("_").pop() as string);
          if (idx !== null) {
            indicesSet.add(idx);
          }
        }
      });
    });
  }
  return {
    indices: Array.from(indicesSet).sort((a, b) => a - b),
    blockedIndices
  };
};

export const applyInputChildrenRules = (
  form: Form | null | undefined,
  response: ResponseRecord,
  responseDelta: ResponseRecord
) => {
  if (!form) {
    return;
  }
  Object.values(form.steps).forEach((step) => {
    step.sections.forEach((section) => {
      section.component_groups.forEach((group) => {
        group.components.forEach((component) => {
          if (
            !Object.prototype.hasOwnProperty.call(responseDelta, component.name)
          ) {
            return;
          }
          if (component.input_children && component.input_children.length) {
            component.input_children.forEach((child) => {
              if (!Object.prototype.hasOwnProperty.call(response, child.name)) {
                return;
              }
              child.rules.forEach((rule) => {
                const parentValue = responseDelta[component.name] as
                  | string
                  | number
                  | boolean
                  | undefined;
                if (
                  parentValue !== undefined &&
                  rule.input_values.includes(parentValue)
                ) {
                  response[child.name] = rule.child_value;
                }
              });
            });
          }
        });
      });
    });
  });
};

const removeGroupValues = (
  group: ComponentGroup,
  response: ResponseRecord,
  partitionKey: string | undefined,
  partitionValue: string | undefined,
  actualTypeKey: string | undefined,
  groupIndices?: Array<string>
) => {
  const baseComponent = group?.components?.[0];
  if (!baseComponent) {
    return;
  }
  if (baseComponent.duplicable) {
    const typeKeys = new Set<string>();
    if (partitionKey) {
      typeKeys.add(partitionKey);
    }
    if (actualTypeKey) {
      typeKeys.add(actualTypeKey);
    }
    const indices =
      groupIndices ||
      collectGroupIndices({
        group,
        response,
        partitionKey,
        partitionValue,
        includeActualTypeKey: false
      });
    indices.forEach((idx) => {
      typeKeys.forEach((key) => {
        delete response[`${key}_${idx}`];
      });
      group.components.forEach((component) => {
        if (component.duplicable) {
          delete response[`${component.name}_${idx}`];
        } else {
          delete response[component.name];
        }
      });
    });
  } else {
    group.components.forEach((component) => {
      delete response[component.name];
    });
  }
};

const removeGroupIndex = (
  group: ComponentGroup,
  response: ResponseRecord,
  partitionKey: string | undefined,
  actualTypeKey: string | undefined,
  idx: string
) => {
  const typeKeys = new Set<string>();
  if (partitionKey) {
    typeKeys.add(partitionKey);
  }
  if (actualTypeKey) {
    typeKeys.add(actualTypeKey);
  }
  typeKeys.forEach((key) => {
    delete response[`${key}_${idx}`];
  });
  group.components.forEach((component) => {
    if (component.duplicable) {
      delete response[`${component.name}_${idx}`];
    } else {
      delete response[component.name];
    }
  });
};

const removeComponentValues = (
  component: FormComponent,
  response: ResponseRecord,
  groupIndices: Array<string>
) => {
  if (component.duplicable && groupIndices.length) {
    groupIndices.forEach((idx) => {
      delete response[`${component.name}_${idx}`];
    });
  } else {
    delete response[component.name];
  }
};

export const normalizeDuplicableGroups = (
  form: Form | null | undefined,
  response: ResponseRecord
) => {
  if (!form) {
    return;
  }
  const signatureUsage = buildSignatureUsage(form);
  const storedResponse = { ...response };
  const mergedResponse = response;
  Object.values(form.steps).forEach((step, stepIndex) => {
    step.sections.forEach((section, sectionIndex) => {
      section.component_groups.forEach((group, groupIndex) => {
        const baseComponent = group.components?.[0];
        if (!baseComponent?.duplicable || !baseComponent?.bound) {
          return;
        }
        const {
          actualTypeKey,
          actualGroupType,
          partitionKey,
          partitionValue,
          usePartitionKey,
          useActualTypeKey
        } = resolveGroupMeta({
          group,
          signatureUsage,
          groupId: `${stepIndex}_${sectionIndex}_${groupIndex}`
        });
        if (
          baseComponent.validation_rules?.length &&
          !shouldShowByRules(baseComponent, mergedResponse, storedResponse)
        ) {
          const currentIndices = collectGroupIndices({
            group,
            response: mergedResponse,
            partitionKey: usePartitionKey ? partitionKey : undefined,
            partitionValue: usePartitionKey ? partitionValue : undefined,
            includeActualTypeKey: false
          });
          removeGroupValues(
            group,
            response,
            usePartitionKey ? partitionKey : undefined,
            usePartitionKey ? partitionValue : undefined,
            actualTypeKey,
            currentIndices
          );
          return;
        }

        const boundKey = baseComponent.bound as string;
        const boundValue =
          mergedResponse[boundKey] !== undefined
            ? mergedResponse[boundKey]
            : storedResponse[boundKey];
        const boundExists =
          Object.prototype.hasOwnProperty.call(mergedResponse, boundKey) ||
          Object.prototype.hasOwnProperty.call(storedResponse, boundKey);
        let desiredCount = getDesiredCount(boundValue, boundExists);
        const { indices, blockedIndices } = buildIndexSets(
          group,
          mergedResponse,
          usePartitionKey ? partitionKey : undefined,
          usePartitionKey ? partitionValue : undefined
        );
        if (!boundExists) {
          desiredCount = indices.length;
        }
        if (indices.length > desiredCount) {
          const toRemove = indices.slice(desiredCount);
          toRemove.forEach((idx) => {
            removeGroupIndex(
              group,
              response,
              usePartitionKey ? partitionKey : undefined,
              actualTypeKey,
              formatIndex(idx)
            );
          });
        }
        const activeIndices = indices.slice(0, desiredCount);
        let nextIndex = activeIndices.length
          ? activeIndices[activeIndices.length - 1] + 1
          : 1;
        while (activeIndices.length < desiredCount) {
          if (!blockedIndices.has(nextIndex)) {
            activeIndices.push(nextIndex);
          }
          nextIndex += 1;
        }

        activeIndices.forEach((idx) => {
          const formatted = formatIndex(idx);
          if (usePartitionKey && partitionKey && partitionValue) {
            const typeKeyName = `${partitionKey}_${formatted}`;
            response[typeKeyName] = partitionValue;
            mergedResponse[typeKeyName] = partitionValue;
          }
          if (useActualTypeKey && actualTypeKey && actualGroupType) {
            const actualKeyName = `${actualTypeKey}_${formatted}`;
            response[actualKeyName] = actualGroupType;
            mergedResponse[actualKeyName] = actualGroupType;
          }
          group.components.forEach((component) => {
            if (!component.duplicable) {
              return;
            }
            if (
              component.validation_rules?.length &&
              !shouldShowByRules(component, mergedResponse, storedResponse)
            ) {
              delete response[`${component.name}_${formatted}`];
              return;
            }
            const key = `${component.name}_${formatted}`;
            if (response[key] === undefined) {
              const fallback =
                mergedResponse[key] !== undefined
                  ? mergedResponse[key]
                  : component.value;
              response[key] = fallback;
              mergedResponse[key] = fallback;
            }
          });
        });
      });
    });
  });
};

export const pruneHiddenGroups = (
  form: Form | null | undefined,
  response: ResponseRecord
) => {
  if (!form) {
    return;
  }
  const signatureUsage = buildSignatureUsage(form);
  const storedResponse = { ...response };
  Object.values(form.steps).forEach((step, stepIndex) => {
    step.sections.forEach((section, sectionIndex) => {
      section.component_groups.forEach((group, groupIndex) => {
        const baseComponent = group.components?.[0];
        if (!baseComponent) {
          return;
        }
        const {
          actualTypeKey,
          partitionKey,
          partitionValue,
          usePartitionKey
        } = resolveGroupMeta({
          group,
          signatureUsage,
          groupId: `${stepIndex}_${sectionIndex}_${groupIndex}`
        });
        const groupIndices = baseComponent.duplicable
          ? collectGroupIndices({
            group,
            response,
            partitionKey: usePartitionKey ? partitionKey : undefined,
            partitionValue: usePartitionKey ? partitionValue : undefined,
            includeActualTypeKey: false
          })
          : [];
        if (
          baseComponent.validation_rules?.length &&
          !shouldShowByRules(baseComponent, response, storedResponse)
        ) {
          removeGroupValues(
            group,
            response,
            usePartitionKey ? partitionKey : undefined,
            usePartitionKey ? partitionValue : undefined,
            actualTypeKey,
            groupIndices
          );
          return;
        }
        group.components.forEach((component) => {
          if (
            component.validation_rules?.length &&
            !shouldShowByRules(component, response, storedResponse)
          ) {
            removeComponentValues(component, response, groupIndices);
          }
        });
      });
    });
  });
};

export const stripInternalDuplicateKeys = (
  response: Record<string, unknown>
) => {
  if (!response || typeof response !== "object") {
    return {};
  }
  return Object.entries(response).reduce((acc, [key, value]) => {
    if (!key.startsWith(INTERNAL_DUP_PREFIX)) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
};

/**
 * Parcourt le form à la recherche de rules minCreditCount et vérifie
 * que le payload respecte la contrainte (somme des crédits > 0).
 * Retourne true si valide, false si le payload viole une contrainte.
 */
export const validateMinCreditCounts = (
  form: Form | null | undefined,
  response: Record<string, unknown>
): boolean => {
  if (!form || !response) {
    return true;
  }
  const minCreditRegex = /^minCreditCount\((.+)\)$/;
  const violations: string[][] = [];

  Object.values(form.steps).forEach((step) => {
    step.sections.forEach((section) => {
      section.component_groups.forEach((group) => {
        group.components.forEach((component) => {
          if (!component.rules || !Array.isArray(component.rules)) {
            return;
          }
          component.rules.forEach((rule) => {
            const ruleValue = Object.values(rule)[0] as string;
            const match = minCreditRegex.exec(ruleValue);
            if (!match) {
              return;
            }
            const paramStr = match[1];
            const fields = paramStr
              .replaceAll("'", "")
              .replaceAll("[", "")
              .replaceAll("]", "")
              .split(", ");
            const total = fields.reduce((sum, field) => {
              const val = Number(response[field] ?? 0);
              return sum + (Number.isNaN(val) ? 0 : val);
            }, 0);
            if (total <= 0) {
              violations.push(fields);
            }
          });
        });
      });
    });
  });

  return violations.length === 0;
};
